<template>
  <div id="features-panel">
    <header class="row mb-2">
      <div class="col-sm-8">
        <slot>
          <h5>{{ $t("featuresPanel.featuresManagement") }}</h5>
        </slot>
      </div>
      <div class="text-sm-right col-sm-4">
        <BButton :disabled="!isDirty" class="pull-right" size="sm" variant="light" @click="onReset">
          <font-awesome-icon icon="times"></font-awesome-icon>
        </BButton>
        <BButton
          :disabled="!isDirty"
          class="pull-right ml-1"
          size="sm"
          variant="primary"
          @click="onSave"
          style="background-color: #0054a6"
        >
          <font-awesome-icon icon="save"></font-awesome-icon>
        </BButton>
      </div>
    </header>

    <BOverlay v-if="!disabled" :show="formLoading" rounded="sm" variant="white" opacity="0.6" blur="2px">
      <BCard
        :title="group.displayName ?? undefined"
        v-for="group in groups"
        :key="group.name ?? undefined"
        class="mb-1"
      >
        <BCardBody>
          <BFormGroup
            v-for="feature in group.features"
            :key="feature.name ?? undefined"
            :label="feature.displayName ?? undefined"
          >
            <BFormCheckbox
              v-if="feature.valueType?.name === 'ToggleStringValueType'"
              v-model="feature.value"
              value="true"
              unchecked-value="false"
              size="md"
              switch
            >
              {{ feature.value === "true" ? "Enabled" : "Disabled" }}
            </BFormCheckbox>
            <div class="mb-3" v-else-if="feature.valueType?.name === 'SelectionStringValueType'">
              <select id="SelectModal" v-model="feature.value" aria-label="Select Feature">
                <option v-for="item in itemSource(feature.valueType)" :key="item.value" :value="item.value">
                  {{ item.displayText.name }}
                </option>
              </select>
            </div>
            <BFormInput v-else v-model="feature.value" type="text"></BFormInput>
          </BFormGroup>
        </BCardBody>
      </BCard>
    </BOverlay>
    <p v-else class="text-center mt-4">{{ $t("featuresPanel.nothingToShow") }}</p>
  </div>
</template>

<script lang="ts">
import { FeatureGroupDto, GetFeatureListResultDto, UpdateFeaturesDto } from "@/api/model";
import { FeaturesApi } from "@/api";
import isEqual from "lodash/isEqual";
import { defineComponent } from "vue";
import { useToast } from "vue-toastification";
import { BCardBody, BFormCheckbox, BFormGroup, BFormInput, BButton, BOverlay, BCard } from "bootstrap-vue-next";

export default defineComponent({
  name: "FeaturesPanel",
  components: {
    BCardBody,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BButton,
    BOverlay,
    BCard
  },
  props: {
    disabled: {
      type: Boolean,
      required: true
    },
    providerName: {
      type: String,
      required: true
    },
    providerKey: {
      type: String
    }
  },
  async mounted(): Promise<void> {
    if (!this.disabled) {
      await this.readFeatures();
    }
  },
  data() {
    return {
      savedFeatures: null as GetFeatureListResultDto | null,
      features: null as GetFeatureListResultDto | null,
      formLoading: false,
      toast: null as any
    };
  },
  setup() {
    const featuresApi = new FeaturesApi()!;
    return {
      featuresApi
    };
  },
  computed: {
    groups(): FeatureGroupDto[] {
      return this.features?.groups ?? [];
    },
    isDirty(): boolean {
      return !isEqual(this.features, this.savedFeatures);
    },
    itemSource(valueType?: any) {
      return valueType["itemSource"]["items"];
    }
  },
  methods: {
    async readFeatures(): Promise<void> {
      this.formLoading = true;
      const data = await this.featuresApi.apiFeatureManagementFeaturesGet(this.providerName, this.providerKey);
      this.features = data.data;
      this.savedFeatures = structuredClone(data.data);
      this.formLoading = false;
    },

    async onSave(): Promise<void> {
      this.formLoading = true;

      const updateFeaturesDto: UpdateFeaturesDto = {
        features: []
      };

      this.features?.groups?.forEach(group => {
        group.features?.forEach(feature => {
          updateFeaturesDto.features?.push({
            name: feature.name,
            value: feature.value
          });
        });
      });

      await this.featuresApi
        .apiFeatureManagementFeaturesPut(this.providerName, this.providerKey, updateFeaturesDto)
        .then(async () => {
          this.toast.success("Features Updated", {
            toastClassName: "custom-success-toast",
            timeout: 5000
          });
          await this.readFeatures();
        })
        .catch(e => {
          this.toast.error(e.errorMsg, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        })
        .finally(() => {
          this.formLoading = false;
        });
    },

    onReset(): void {
      this.features = structuredClone(this.savedFeatures);
    }
  },
  created() {
    this.toast = useToast();
  }
});
</script>
